import React from 'react';
import { getStock } from '../data/sampleStock';

const locationCode = (warehouse, selection, ...parts) =>
  [
    warehouse.name.toUpperCase(),
    selection.zoneKey.toUpperCase(),
    `${selection.type === 'rack' ? 'R' : 'A'}${selection.index}`,
    ...parts,
  ].join('-');

// Selection #2: shelves/slots for a rack, or floor slots for an aisle.
const StorageSelector = ({ warehouse, selection, location, onSelectLocation }) => {
  if (!selection) {
    return <div className="hint">Select a rack or aisle above to see its shelves / floor slots.</div>;
  }
  const zone = warehouse.zones.find((z) => z.key === selection.zoneKey);
  const title = `${zone.label} – ${selection.type === 'rack' ? 'Rack' : 'Aisle'} ${selection.index}`;

  const pick = (code, label) => onSelectLocation({ code, label: `${title} – ${label}`, zoneKey: zone.key });
  const isSel = (code) => location && location.code === code;

  if (selection.type === 'aisle') {
    // No shelf layout for aisles: goods are laid directly on floor slots.
    return (
      <div className="storage">
        <h3>{title}</h3>
        <div className="floor-slots">
          {Array.from({ length: zone.floorSlotsPerAisle }, (_, i) => {
            const code = locationCode(warehouse, selection, `F${i + 1}`);
            return (
              <button
                key={code}
                type="button"
                className={`slot floor${isSel(code) ? ' selected' : ''}`}
                onClick={() => pick(code, `Floor Slot ${i + 1}`)}
              >
                Floor {i + 1}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Rack: top shelf drawn first.
  const shelves = Array.from({ length: zone.shelvesPerRack }, (_, i) => zone.shelvesPerRack - i);
  return (
    <div className="storage">
      <h3>{title}</h3>
      <div className={`rack-frame rack-${zone.key}`}>
        {shelves.map((s) => (
          <div key={s} className="shelf">
            <div className="shelf-label">Shelf {s}</div>
            {zone.usesPallets ? (
              <div className="slots">
                {Array.from({ length: zone.slotsPerShelf }, (_, i) => {
                  const code = locationCode(warehouse, selection, `S${s}`, `P${i + 1}`);
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`slot pallet${isSel(code) ? ' selected' : ''}`}
                      onClick={() => pick(code, `Shelf ${s} / Slot ${i + 1}`)}
                    >
                      Slot {i + 1}
                    </button>
                  );
                })}
              </div>
            ) : (
              // Chilled / frozen: the shelf itself is the location (no pallets).
              (() => {
                const code = locationCode(warehouse, selection, `S${s}`);
                return (
                  <button
                    type="button"
                    className={`slot shelf-only${isSel(code) ? ' selected' : ''}`}
                    onClick={() => pick(code, `Shelf ${s}`)}
                  >
                    Whole shelf
                  </button>
                );
              })()
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const LocationDetail = ({ location }) => {
  if (!location) return <div />;
  const stock = getStock(location.code, location.zoneKey);
  return (
    <div className="detail">
      <h3>{location.label}</h3>
      <div className="code">{location.code}</div>
      {stock.length === 0 ? (
        <div className="hint">Empty</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Qty</th>
            </tr>
          </thead>
          <tbody>
            {stock.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td>{p.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default StorageSelector;
